import React, { useState } from 'react';
import { useDraggable } from '@dnd-kit/core';
import type { ExpressionConstant } from '../../state/types';
import AddConstantDialog from './AddConstantDialog';

function PencilIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
      <path d="m15 5 4 4" />
    </svg>
  );
}

function TrashIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <line x1="10" y1="11" x2="10" y2="17" />
      <line x1="14" y1="11" x2="14" y2="17" />
    </svg>
  );
}

type DraggableConstantItemProps = ExpressionConstant & {
  setDraggedItem: (item: {
    type: string;
    label: string;
    constantId?: string;
    value?: number | string;
    constantType?: 'number' | 'string';
  } | null) => void;
};

const DraggableConstantItem: React.FC<DraggableConstantItemProps> = ({
  id,
  name,
  type,
  value,
  setDraggedItem,
}) => {
  const constantType: 'number' | 'string' = type === 'dayOfWeek' ? 'string' : 'number';
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id,
    data: { type: 'constant', constantId: id, value, constantType },
  });

  return (
    <div
      ref={setNodeRef}
      className={`flex items-center gap-2 group ${isDragging ? 'opacity-50' : ''}`}
    >
      <div
        {...listeners}
        {...attributes}
        onMouseDown={() =>
          setDraggedItem({ type: 'constant', label: name, constantId: id, value, constantType })
        }
        onMouseUp={() => setDraggedItem(null)}
        className="flex-1 min-w-0 cursor-move px-2 py-1 rounded-sm border border-rule text-sm bg-newsprint-dark hover:bg-newsprint italic text-ink"
      >
        {name}
      </div>
    </div>
  );
};

type Props = {
  constants: ExpressionConstant[];
  setDraggedItem: (item: {
    type: string;
    label: string;
    constantId?: string;
    value?: number | string;
    constantType?: 'number' | 'string';
  } | null) => void;
  onAddConstant: (constant: Omit<ExpressionConstant, 'id'>) => void;
  onUpdateConstant: (id: string, updates: Partial<Omit<ExpressionConstant, 'id'>>) => void;
  onRemoveConstant: (id: string) => void;
};

const AvailableConstants: React.FC<Props> = ({
  constants,
  setDraggedItem,
  onAddConstant,
  onUpdateConstant,
  onRemoveConstant,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingConstant, setEditingConstant] = useState<ExpressionConstant | null>(null);
  const existingNames = constants.map((c) => c.name);

  const openAdd = () => {
    setEditingConstant(null);
    setDialogOpen(true);
  };

  const openEdit = (c: ExpressionConstant) => {
    setEditingConstant(c);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingConstant(null);
  };

  return (
    <div>
      <h3 className="font-bold mb-2">Constants</h3>
      <div className="flex flex-col gap-2">
        <button
          type="button"
          onClick={openAdd}
          className="w-full px-2 py-1 rounded-sm border border-dashed border-rule text-sm text-ink/80 hover:bg-newsprint-dark hover:border-ink/50 transition text-left"
        >
          + Add Constant
        </button>
        {constants.map((c) => (
          <div key={c.id} className="flex items-center gap-1">
            <DraggableConstantItem
              id={c.id}
              name={c.name}
              type={c.type}
              value={c.value}
              setDraggedItem={setDraggedItem}
            />
            <button
              type="button"
              onClick={() => openEdit(c)}
              className="p-1 rounded-sm text-ink/60 hover:text-ink hover:bg-newsprint-dark transition shrink-0"
              aria-label="Update constant"
              title="Update constant"
            >
              <PencilIcon className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => onRemoveConstant(c.id)}
              className="p-1 rounded-sm text-ink/60 hover:text-error hover:bg-newsprint-dark transition shrink-0"
              aria-label="Remove constant"
              title="Remove constant"
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
      <AddConstantDialog
        isOpen={dialogOpen}
        existingNames={existingNames}
        editingConstant={editingConstant}
        onSubmit={(form, constantId) => {
          if (constantId) {
            onUpdateConstant(constantId, form);
          } else {
            onAddConstant(form);
          }
          closeDialog();
        }}
        onCancel={closeDialog}
      />
    </div>
  );
};

export default AvailableConstants;
